import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, FileDown, CheckCircle, CreditCard } from 'lucide-react';
import { useBulletinsParPeriode, useChangerStatutBulletin, useChangerStatutBulletinsMasse } from '../hooks/useBulletins';
import { useBulletinsStore } from '../store/bulletins.store';
import { PeriodeSelector } from '../components/PeriodeSelector';
import { useProjets, useProjet } from '../../administration/hooks/useProjets';
import { useEmployes } from '../../employes/hooks/useEmployes';
import { IconButton } from '../../../shared/components/ui/IconButton';

export function BulletinsListePage() {
  const { filtres, setFiltres } = useBulletinsStore();
  const [recherche, setRecherche] = useState('');
  const [erreurDocx, setErreurDocx] = useState<Record<string, string>>({});

  const { data: projets } = useProjets({ actif: true });
  const listeProjets = (projets as any[]) ?? [];
  const estFiltreAucunProjet = filtres.idProjet === '__AUCUN__';
  const { data: projetDetail, isLoading: projetCharge } = useProjet(estFiltreAucunProjet ? undefined : filtres.idProjet);
  const { data: employesSansProjet, isLoading: employesSansProjetCharge } = useEmployes(
    { idProjet: '__AUCUN__', actif: true },
    { enabled: estFiltreAucunProjet }
  );

  const idsEmployesProjet: null | 'chargement' | Set<string> = estFiltreAucunProjet
    ? employesSansProjetCharge
      ? 'chargement'
      : new Set((employesSansProjet?.items ?? []).map((e: any) => e.idEmploye))
    : filtres.idProjet
      ? projetDetail
        ? new Set((projetDetail as any).affectations?.map((a: any) => a.idEmploye) ?? [])
        : 'chargement'
      : null;

  const { data: bulletins = [], isLoading, isError } = useBulletinsParPeriode(filtres.annee, filtres.mois);
  const changerStatut = useChangerStatutBulletin();
  const changerStatutMasse = useChangerStatutBulletinsMasse();
  const [erreurMasse, setErreurMasse] = useState('');
  const [successMasse, setSuccessMasse] = useState('');

  const handleChangerStatutMasse = async (statut: 'VALIDE' | 'PAYE') => {
    setErreurMasse('');
    setSuccessMasse('');
    try {
      const result: any = await changerStatutMasse.mutateAsync({ mois: filtres.mois, annee: filtres.annee, statut });
      setSuccessMasse(`${result?.nombreMisAJour ?? 0} bulletin(s) passé(s) au statut ${statut}`);
    } catch (err: any) {
      setErreurMasse(err?.response?.data?.message || `Erreur lors du passage au statut ${statut}`);
    }
  };

  const bulletinsFiltres = idsEmployesProjet === 'chargement'
    ? []
    : bulletins.filter(b => {
        const matchRecherche = `${b.nomEmploye} ${b.prenomEmploye}`.toLowerCase().includes(recherche.toLowerCase());
        const matchProjet = !idsEmployesProjet || (idsEmployesProjet as Set<string>).has(b.idEmploye);
        return matchRecherche && matchProjet;
      });

  const handleTelecharger = async (idBulletin: string, nom: string) => {
    setErreurDocx(prev => ({ ...prev, [idBulletin]: '' }));
    try {
      const { bulletinsApi } = await import('../api/bulletins.api');
      const blob = await bulletinsApi.telecharger(idBulletin);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulletin-${nom}-${filtres.mois}-${filtres.annee}.docx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setErreurDocx(prev => ({ ...prev, [idBulletin]: err?.response?.data?.message || 'Erreur DOCX' }));
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-content">Bulletins de paie</h1>
        <div className="flex gap-2">
          <Link to="/paie/bulletins/generer" className="bg-accent text-content-inverted px-4 py-2 rounded hover:bg-primary-hover text-sm font-medium">
            + Générer un bulletin
          </Link>
          <Link to="/paie/bulletins/masse" className="bg-content-muted text-content-inverted px-4 py-2 rounded hover:bg-content text-sm font-medium">
            Génération en masse
          </Link>
        </div>
      </div>

      {/* Actions en masse */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
        <span className="text-sm font-medium text-slate-600">Actions sur la période :</span>
        <button
          onClick={() => handleChangerStatutMasse('VALIDE')}
          disabled={changerStatutMasse.isPending}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          ✓ Valider tous
        </button>
        <button
          onClick={() => handleChangerStatutMasse('PAYE')}
          disabled={changerStatutMasse.isPending}
          className="flex items-center gap-1.5 px-4 py-1.5 bg-accent text-content-inverted text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-50 transition-colors"
        >
          💳 Marquer tous payés
        </button>
        {changerStatutMasse.isPending && <span className="text-xs text-slate-500">Traitement en cours…</span>}
        {successMasse && <span className="text-xs text-emerald-700 font-medium">{successMasse}</span>}
        {erreurMasse && <span className="text-xs text-danger">{erreurMasse}</span>}
      </div>

      <div className="flex gap-4 mb-4 flex-wrap">
        <PeriodeSelector mois={filtres.mois} annee={filtres.annee} onChange={(m, a) => setFiltres({ mois: m, annee: a })} />
        {listeProjets.length > 0 && (
          <select
            value={filtres.idProjet ?? ''}
            onChange={e => setFiltres({ idProjet: e.target.value || undefined })}
            className="border border-border rounded px-3 py-2 text-sm"
          >
            <option value="">Tous les projets</option>
            <option value="__AUCUN__">Aucun projet</option>
            {listeProjets.map((p: any) => (
              <option key={p.idProjet} value={p.idProjet}>{p.nom}</option>
            ))}
          </select>
        )}
        <input
          type="text"
          placeholder="Rechercher un employé..."
          value={recherche}
          onChange={e => setRecherche(e.target.value)}
          className="border border-border rounded px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <Link to={`/paie/bulletins/tableau/${filtres.annee}/${filtres.mois}`} className="text-sm text-accent border border-blue-600 px-3 py-2 rounded hover:bg-accent-light">
          Tableau d'état
        </Link>
      </div>

      {isLoading && <p className="text-content-subtle text-sm">Chargement...</p>}
      {isError && <p className="text-danger text-sm">Erreur lors du chargement des bulletins.</p>}

      {!isLoading && bulletinsFiltres.length === 0 && (
        <div className="text-center py-12 text-content-subtle">
          <p className="text-lg">Aucun bulletin pour cette période.</p>
          <p className="text-sm mt-1">Sélectionnez une autre période ou générez des bulletins.</p>
        </div>
      )}

      {bulletinsFiltres.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-raised">
              <tr>
                {['Employé', 'Période', 'Brut', 'CNSS', 'ITS', 'Net', 'À payer', 'Statut'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-content-subtle uppercase tracking-wider">{h}</th>
                ))}
                <th className="px-4 py-3 text-xs font-medium text-content-subtle uppercase tracking-wider"></th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-border">
              {bulletinsFiltres.map(b => (
                <tr key={b.idBulletin} className="hover:bg-surface-raised">
                  <td className="px-4 py-3 text-sm font-medium text-content">{b.nomEmploye} {b.prenomEmploye}</td>
                  <td className="px-4 py-3 text-sm text-content-muted">{b.mois}/{b.annee}</td>
                  <td className="px-4 py-3 text-sm text-content">{b.salaireBrut.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3 text-sm text-danger">{b.montantCNSS.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3 text-sm text-danger">{b.montantITS.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3 text-sm text-content">{b.salaireNet.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3 text-sm font-semibold text-success">{b.netAPayer.toLocaleString('fr-FR')} F</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      b.statut === 'PAYE' ? 'bg-accent-light text-accent'
                      : b.statut === 'VALIDE' ? 'bg-success-light text-success'
                      : 'bg-warning-light text-warning'
                    }`}>
                      {b.statut}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1 items-center">
                        <IconButton icon={Eye} label="Voir" href={`/paie/bulletins/${b.idBulletin}`} />
                        <IconButton
                          icon={FileDown}
                          label="Télécharger DOCX"
                          onClick={() => handleTelecharger(b.idBulletin, b.nomEmploye)}
                        />
                        {(b.statut === 'BROUILLON' || b.statut === 'GENERE') && (
                          <IconButton
                            icon={CheckCircle}
                            label="Valider"
                            variant="success"
                            disabled={changerStatut.isPending}
                            onClick={() => changerStatut.mutate({ idBulletin: b.idBulletin, statut: 'VALIDE' })}
                          />
                        )}
                        {b.statut === 'VALIDE' && (
                          <IconButton
                            icon={CreditCard}
                            label="Marquer payé"
                            variant="warning"
                            disabled={changerStatut.isPending}
                            onClick={() => changerStatut.mutate({ idBulletin: b.idBulletin, statut: 'PAYE' })}
                          />
                        )}
                      </div>
                      {erreurDocx[b.idBulletin] && (
                        <span className="text-xs text-danger">{erreurDocx[b.idBulletin]}</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
